import { CircleInfoIcon } from "@xola/icons";
import { Alert, Switch } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, formatDate, isPartnerPackageProduct, isSame, isToday, now, PurchaseItemStatus } from "common";
import { Dayjs, isDayjs } from "dayjs";
import { find, isEmpty, isEqual, isNil, isNumber } from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    AllowedPrivacy,
    Arrival,
    ArrivalError,
    ExperiencePurchaseItem,
    Permission,
    PurchaseItemPrivacy,
    ScheduleType,
    Timeslot,
} from "types";
import { useDebouncedCallback } from "use-debounce";
import shallow from "zustand/shallow";
import { useSeller, useUser } from "../../hooks/useAccount";
import { useAuthorization } from "../../hooks/useAuthorization";
import { useModal } from "../../hooks/useVisibility";
import { isTimeslotInProgress } from "../../models/Timeslot";
import { CreatePurchaseItemState, useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { ManageActionButton } from "../Dashboard/Roster/ManageActionButton";
import { CreateExperienceTimeslotModal } from "../PurchaseModification/CreateExperienceTimeslotModal";
import { ArrivalDatePicker } from "./ArrivalDatePicker";
import {
    getBookableDates,
    getBookableSlots,
    getFirstAvailableTimeslot,
    getNextBookableDate,
    getUnavailabilityMessagesByDate,
    getWaitlistDates,
} from "./ArrivalPickerHelper";
import { ArrivalPickerSkeleton } from "./ArrivalPickerSkeleton";
import { ArrivalRestrictions } from "./ArrivalRestrictions";
import { ArrivalTimePicker } from "./ArrivalTimePicker";
import { OverrideFutureCutoffSwitch } from "./OverrideFutureCutoffSwitch";
import { PastAvailabilitySwitch } from "./PastAvailabilitySwitch";
import { DateOffset, useArrivalTimeslots } from "./useArrivalTimeslots";
import { useLastScheduledDate, useUpcomingDates } from "./useUpcomingDates";
import { useProduct } from "@/hooks/resources/useProduct";
import { isAffiliate } from "@/models/User";

interface ArrivalPickerProps {
    value: Arrival;
    product: ID;
    quantity: number;
    showAddNewTimeSlot: boolean;
    showCapacity?: boolean;
    allowWaitlist?: boolean;
    showAllTimeslotsSwitch?: boolean;
    placement?: string;
    packageId?: ID;
    arrivalErrors?: ArrivalError[];
    privacyPref?: {
        privacy: AllowedPrivacy | PurchaseItemPrivacy;
        allowedPrivacies: Array<AllowedPrivacy | PurchaseItemPrivacy>;
    };
    timezone?: string;
    item?: ExperiencePurchaseItem | CreatePurchaseItemState;
    allowOverlap?: boolean;
    autoSelectTimeslot?: boolean;
    onChange: (value: Arrival, slot?: Timeslot) => void;
}

export const ArrivalPicker = ({
    value,
    product,
    quantity,
    showAddNewTimeSlot,
    showCapacity = true,
    onChange,
    showAllTimeslotsSwitch: showTimeslotsSwitch = false,
    allowWaitlist = false,
    allowOverlap = false,
    placement = "auto",
    privacyPref,
    packageId: pkgId,
    arrivalErrors = [],
    timezone,
    item,
    autoSelectTimeslot = true,
}: ArrivalPickerProps) => {
    const seller = useSeller();
    const user = useUser();
    const { isGranted } = useAuthorization();

    const { datepickerPreference: datePickerPreference } = seller.preferences;
    const [timeslot, setTimeslot] = useCreatePurchaseStore(
        (state) => [state.timeslot ?? null, state.actions.setTimeslot],
        shallow,
    );
    const isMist = useCreatePurchaseStore((state) => state.isMist());

    const [month, setMonth] = useState(now(value.date)); // The month on which the date picker is visible now -- different from selected date
    const upcomingStartDate = formatDate(month.startOf("month")); // Set the start date to the start of the month so we can fetch upcoming dates

    // OffsetDate to store the start and end dates offset
    const initialOffset = { start: DateOffset.ZERO_DAYS, end: DateOffset.SIXTY_TWO_DAYS };
    const offsetDate = useRef(initialOffset);

    const [selectedTimeslots, setSelectedTimeslots] = useState<Timeslot[]>([]);
    const [localTimezone, setLocalTimezone] = useState(timezone);

    useEffect(() => {
        if (value.date && !isSame(month, value.date, "month")) {
            // When the incoming date to this component changes, update the month so that the timeslots API call
            // uses the correct start month
            setMonth(now(value.date));
        }
        setTimeslot(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value.date]);

    const createTimeslotModal = useModal(false);

    const allowPastDates = isGranted(Permission.ALLOW_PAST_DATES);
    const isWaitlistedAllowed = isGranted(Permission.WAITLISTS_VIEW) && allowWaitlist;
    const showAllTimeslotsSwitch = showTimeslotsSwitch || Boolean(datePickerPreference.showUnavailableTimes);
    const allowAllTimeslots = showAllTimeslotsSwitch || allowPastDates;

    // This is times (arrivalTime) only, not for the entire slot
    // By default we show all times only when show waitlist is enabled
    const [showAllTimes, setShowAllTimes] = useState(isWaitlistedAllowed);
    const handleShowAllTimesChange = (value: boolean) => {
        setShowAllTimes(value);
        setSelectedTimeslots([]);
    };

    const initialArrival = item?.arrivalDateTime;
    const isArrivalTimeSet = isNumber(item?.arrivalTime);
    const packageId = pkgId ?? item?.parentProduct?.id;
    const timeslotEnd = now(timeslot?.end).add(1, "second").startOf("minute");
    const timeslotEndFormatted = formatDate(
        timeslotEnd,
        isToday(timeslotEnd) ? DateFormat.TIME : DateFormat.DATE_TIME_SHORT,
    );

    const [isPastAvailabilityChecked, setIsPastAvailabilityChecked] = useState(false);
    const [isOverrideFutureCutoffChecked, setOverrideFutureCutoffChecked] = useState(false);
    const handlePastAvailabilityChange = () => setIsPastAvailabilityChecked(!isPastAvailabilityChecked);
    const handleOverrideFutureCutoffChange = () => setOverrideFutureCutoffChecked(!isOverrideFutureCutoffChecked);

    const isUserAffiliate = isAffiliate(user);

    const { data: lastScheduledDate } = useLastScheduledDate(product, datePickerPreference);

    const {
        data: timeslots,
        isLoading,
        isFetching,
        isFetchingNextPage,
        hasNextPage,
        isStale,
        refetch,
    } = useArrivalTimeslots({
        product,
        quantity,
        start: month,
        showAllTimeslots: showAllTimeslotsSwitch || allowOverlap,
        isPastAvailabilityChecked,
        isOverrideFutureCutoffChecked,
        privacy: privacyPref?.privacy,
        timezone,
        packageId,
        offsetDate: offsetDate.current,
    });

    // Part of timeslot on the fly logic, we have to wait till the timeslots are fetched
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const isOngoingRequest = isFetching || isLoading || hasNextPage || isFetchingNextPage;
    const { data: productData } = useProduct(product);
    const productTimeslots = useMemo(() => {
        return packageId ? timeslots.filter((slot) => slot.product === product) : timeslots;
    }, [packageId, timeslots, product]);
    const next30Days = useMemo(() => now(upcomingStartDate).add(30, "days"), [upcomingStartDate]);
    const { upcomingDates, shouldFetchUpcoming } = useUpcomingDates(
        productTimeslots,
        quantity,
        upcomingStartDate,
        product,
        isOngoingRequest,
    );

    const handleMonthChange = (date: Date | Dayjs) => {
        const selectedMonth = isDayjs(date) ? date : now(date);
        const currentMonth = month.clone(); // Ensure you have a copy of the current month

        // Check if the current month is same as the next month or previous month.
        const isNextMonthSelected = selectedMonth.isSame(currentMonth.add(1, "month"), "month");
        const isLastMonthSelected = selectedMonth.isSame(currentMonth.subtract(1, "month"), "month");
        const isSelectedMonthSameAsPickedDate = currentMonth.isSame(value.date, "month");

        // When we open date picker we fetch 62 days timeslots by default. If next month is selected in date picker we will add offset of 31 days to start date to fetch only one month's dates,
        // Eg: Lets say today is May 23 which is the value of month in date picker by default, initially we fetch timeslots for May 23 + 62 days which is till July 24th.
        // When we change month to June, we will fetch dates only from June 23 + 31 which is July 24th till August 24th i.e only for 31 days.
        // If current month is May and on date picker we select June, we will fetch dates only for July.
        // If previous month is selected the offset start will be zero as we want to fetch dates from Aug 24th you go back to July 24, here we will fetch only for July 24th to Aug 24th.
        // X2-9747: If the selected month is the same as the month of the currently picked date, we will set the default offset (62) to avoid the case when timeslots query key changes, and we fetch just 31 days from the picked date.

        // Determine the offset based on the selected month
        let startOffset;
        let endOffset;
        if (isNextMonthSelected || isLastMonthSelected) {
            startOffset = DateOffset.ZERO_DAYS;
            endOffset = DateOffset.SIXTY_TWO_DAYS;
        } else {
            startOffset = initialOffset.start;
            endOffset = initialOffset.end;
        }

        // Set the offset
        offsetDate.current = isSelectedMonthSameAsPickedDate ? initialOffset : { start: startOffset, end: endOffset };

        // Adjust date to end of selected month if current date exceeds last day of month
        const lastDateOfMonth = selectedMonth.endOf("month").date();
        const currentDate = Math.min(currentMonth.date(), lastDateOfMonth);

        // Update the selected month
        setMonth(selectedMonth.date(currentDate));
    };

    const debouncedOnMonthChange = useDebouncedCallback(handleMonthChange, 200, { leading: false, trailing: true });

    const { bookableDates, waitlistDates, pastDates } = useMemo(() => {
        const [bookableDates, pastDates] = getBookableDates({
            timeslots: productTimeslots,
            product,
            allowPastDates,
            showAllTimeslots: showAllTimes,
            allowOverlap,
            isMist,
            initialArrival,
        });

        const waitlistDates = isWaitlistedAllowed
            ? getWaitlistDates(productTimeslots, bookableDates).filter((date) => !!bookableDates[formatDate(date)])
            : [];

        return { bookableDates, waitlistDates, pastDates };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        productTimeslots,
        product,
        allowPastDates,
        showAllTimes,
        privacyPref?.privacy,
        packageId,
        isMist,
        isWaitlistedAllowed,
    ]);

    const unavailabilityMessagesByDate = useMemo(
        () => getUnavailabilityMessagesByDate(productTimeslots, product),
        [productTimeslots, product],
    );

    const allBookableDates = useMemo(() => {
        return bookableDates[value.date] ?? pastDates[value.date] ?? null;
    }, [bookableDates, pastDates, value.date]);

    // This is use for showing the time picker and has too many dependencies
    const timeslotsOfDay = useCallback(() => {
        const isSelectedTimeslotPresent = find(selectedTimeslots, { date: value.date, time: value.time });
        if (isArrivalTimeSet && isSelectedTimeslotPresent && localTimezone === timezone) {
            return selectedTimeslots;
        }

        return allBookableDates?.filter((timeslot) => {
            const hasOpenSlots = timeslot.capacity.open >= quantity;
            const hasTimeslotsWithoutOpenSlots = allowAllTimeslots && !hasOpenSlots;
            const hideCappedSlots =
                timeslot.capacity.max === 0 && !timeslot.canBook && !allowAllTimeslots && !allowOverlap;
            if (hideCappedSlots) {
                return false;
            }

            const isAvailable = hasOpenSlots || hasTimeslotsWithoutOpenSlots || allowOverlap;
            if (packageId) {
                return isAvailable;
            }

            return isMist ? hasOpenSlots : isAvailable;
        });
    }, [
        allBookableDates,
        value.date,
        value.time,
        allowAllTimeslots,
        quantity,
        allowOverlap,
        isMist,
        packageId,
        isArrivalTimeSet,
        selectedTimeslots,
        localTimezone,
        timezone,
    ]);

    const handleValueChange = useCallback(
        (newValue: Arrival, slot?: Timeslot, clearCurrentDateSlots = false) => {
            const hasSlot = slot ? selectedTimeslots.includes(slot) : true;

            if (newValue.date !== value.date || newValue.time !== value.time || !hasSlot) {
                onChange(newValue, slot);
                if (slot) {
                    setTimeslot(slot);
                }

                setSelectedTimeslots(clearCurrentDateSlots ? [] : timeslotsOfDay() ?? []);
            }
        },
        [onChange, timeslotsOfDay, value.date, value.time, setTimeslot, selectedTimeslots],
    );

    const nextUpcomingDate = upcomingDates && upcomingDates.length > 0 ? upcomingDates[0] : null;

    /**
     * Checks if privacy for the selected timeslot is correct according to the privacy currently selected.
     * if both privacies are allowed we return true, since we don't need to compare.
     * If privacyPref is not passed to the component we will skip this check.
     * If `timeslot.privacy` is not defined this means timeslot available for both `private` and `public`.
     */
    const isBothPrivaciesAllowed = privacyPref?.allowedPrivacies.length === 2;
    const isPrivacyNotDefined = isNil(privacyPref?.privacy) || isNil(timeslot?.privacy);
    const isSamePrivacySlotSelected = timeslot?.privacy === privacyPref?.privacy;
    const isSamePrivacyTimeslot = isBothPrivaciesAllowed || isPrivacyNotDefined || isSamePrivacySlotSelected;

    // We don't need to wait for all API calls to be made to set a slot automatically if it's not set currently.
    // Since we can use data from the 1st API call.
    const skipEffect = (isOngoingRequest || isStale) && !isFetchingNextPage;
    const isConfirmedItemStatus = item?.status === PurchaseItemStatus.CONFIRMED || (item && "createdAt" in item);

    useEffect(() => {
        const bookableSlots = getBookableSlots(value.date, bookableDates);

        // If the purchase has a CONFIRMED status, we don't need to reset the date again (except when there is only one slot).
        if (skipEffect || (isConfirmedItemStatus && bookableSlots.length !== 1)) {
            return;
        }

        /** First bookable timeslot after seller's current time. */
        const nextBookableTimeslot = getFirstAvailableTimeslot(bookableSlots, isPastAvailabilityChecked, value.date);

        if (bookableSlots.length > 0 && nextBookableTimeslot) {
            const { date, time } = nextBookableTimeslot;

            /**
             * Checks if we should auto-select timeslot instead currently selected.
             * If `timeslot.product` is not the same as product passed, the package/mist booking case, when we can have slot from another experience.
             * If `timeslot.privacy` is not the same as privacy passed, since timeslot is timeslot public only we need to reselect from available ones and vice versa.
             * If `item.arrivalTime` or selected timeslot `time` is set, but current value missing time, we need to auto select first available (switch experience, upcoming date cases).
             */
            const isValueMissingTime = (!isNil(item?.arrivalTime) || !isNil(timeslot?.time)) && isNil(value.time);
            const shouldSetTimeslot =
                autoSelectTimeslot && (timeslot?.product !== product || !isSamePrivacyTimeslot || isValueMissingTime);

            if (shouldSetTimeslot && !item?.arrivalTime) {
                // Handles case when both timeslot and arrivalTime is not set.
                handleValueChange({ date, time }, nextBookableTimeslot);
            } else if (shouldSetTimeslot && item?.arrivalTime) {
                // Handles case when user redirected from the dashboard with predefined time,
                // if initial timeslot is not available select the next bookable.
                const selectedSlot = find(bookableSlots, { time: item.arrivalTime, date: item.arrivalDate });
                const arrivalTime = selectedSlot ? item.arrivalTime : time;
                const timeslot = selectedSlot ? selectedSlot : nextBookableTimeslot;
                handleValueChange({ date, time: arrivalTime }, timeslot);
            }
        } else if (!timeslot?.canBook && !isEmpty(bookableDates) && autoSelectTimeslot) {
            // The selected bookable date is not available. Select the next one after value.date
            const nextBookableDate = getNextBookableDate(value.date, bookableDates);
            if (nextBookableDate) {
                const { date, time, timeslot } = nextBookableDate;
                handleValueChange({ date, time }, timeslot);
            }
        } else if (nextUpcomingDate && bookableSlots.length === 0 && nextUpcomingDate !== month.format("YYYY-MM-DD")) {
            setMonth(now(nextUpcomingDate));
        }
        // Don't add timeslot as a dependency as it conflicting if we have multiple Products in Package (X2-9730)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        value.date,
        value.time,
        bookableDates,
        nextUpcomingDate,
        skipEffect,
        item?.arrivalDate,
        item?.arrivalTime,
        autoSelectTimeslot,
        product,
        handleValueChange,
        isPastAvailabilityChecked,
        isOverrideFutureCutoffChecked,
    ]);

    const handleDayChange = (value: Date, options: { disabled: boolean }) => {
        if (options && !options.disabled) {
            const date = formatDate(value);

            // `getBookableSlots` can return a slot for another date,
            // if the value passed has the date in the future (2 or more months in advance),
            // so we need to explicitly filter for passed date slots to avoid the case when we select the date,
            // and the time which is not available for selected date.
            const slots = getBookableSlots(date, bookableDates).filter((slot) => slot.date === date);

            const availableSlot =
                slots.find((slot) => slot.capacity.open >= quantity) ?? allowWaitlist ? slots[0] : undefined;

            // The date is changed, so we need to unselect the timeslot.
            // No timezone change required because we have already set the default timezone when the user logged in
            const { time } =
                slots.length > 0 && availableSlot && !isConfirmedItemStatus ? availableSlot : { time: null }; // set time to bookable timeslot
            handleValueChange({ date, time }, availableSlot, true);
        }
    };

    const handleTimeslotChange = (time: number) => {
        const slot = getBookableSlots(value.date, bookableDates).find((slot) => slot.time === time);
        handleValueChange({ ...value, time }, slot);
    };

    const currentDateTimeslots = timeslotsOfDay()?.filter((timeslot: Timeslot) => timeslot.time !== null) ?? [];
    const hasTimeslot = currentDateTimeslots.length > 0;
    const shouldUpdateSelectedDateTimeslots = isArrivalTimeSet && hasTimeslot && autoSelectTimeslot;

    /**
     * Used only to check if need to re-render timeslots of the day,
     * since we need to preserve timeslots of the selected date when we switch calendar months,
     * but we need to calculate available timeslots if privacy changes.
     */
    const previousPrivacyRef = useRef(privacyPref?.privacy);
    const isPrivacyChanged = previousPrivacyRef.current !== privacyPref?.privacy;

    useEffect(() => {
        if (isPrivacyChanged) {
            setSelectedTimeslots([]);
            previousPrivacyRef.current = privacyPref?.privacy;

            const bookableSlots = getBookableSlots(value.date, bookableDates);
            const nextBookableTimeslot = getFirstAvailableTimeslot(
                bookableSlots,
                isPastAvailabilityChecked,
                value.date,
            );

            const currentTimeslot = bookableSlots.find((bookableSlot) => bookableSlot.id === timeslot?.id);

            if (nextBookableTimeslot && !currentTimeslot?.canBook) {
                const { date, time } = nextBookableTimeslot;
                handleValueChange({ date, time }, nextBookableTimeslot);
            }
        } else if (shouldUpdateSelectedDateTimeslots) {
            // If timeslots have changed, update the state. But we should do it carefully to not get into an
            // infinite loop of re-renders
            if (selectedTimeslots.length !== currentDateTimeslots.length) {
                setSelectedTimeslots(currentDateTimeslots);
            } else if (!isEqual(currentDateTimeslots, selectedTimeslots)) {
                // Only execute isEqual if we really need it
                setSelectedTimeslots(currentDateTimeslots);
            }

            if (timezone !== localTimezone) {
                setLocalTimezone(timezone);
            }
        }
        /**
         * The only triggers should be `productTimeslots` and `shouldUpdateSelectedDateTimeslots`.
         * Not adding `currentDateTimeslots` to avoid possible loop between this and `timeslotsOfDay`.
         * Not adding `isPrivacyChanged` to avoid non-needed re-renders since we already re-fetch timeslots on privacy change.
         * Not adding `previousPrivacyRef.current` since it causes unexpected behavior, and is not required by the below lint rule.
         * Not adding `privacy` since it causes more re-renders,
         * and during the initial render cause an issue when slots with incorrect privacy might be displayed until you switch privacy again.
         */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productTimeslots, shouldUpdateSelectedDateTimeslots]);

    if (isLoading) {
        return <ArrivalPickerSkeleton />;
    }

    const handleDisabledDays = (date: Date) => {
        const dt = formatDate(date);
        const cannotBookDate = !bookableDates[dt];
        if (isPastAvailabilityChecked) {
            return cannotBookDate;
        }

        if (isOngoingRequest) {
            return cannotBookDate;
        }

        const isPastDate = pastDates[dt];
        return cannotBookDate || Boolean(isPastDate);
    };

    const handleLoadingDays = (date: Date) => {
        const dt = formatDate(date);
        const cannotBookDate = !bookableDates[dt];

        if (isOngoingRequest) {
            return cannotBookDate;
        }

        return false;
    };

    const handleNewTimeslotDisabledDays = (date: Date) => {
        return isPastAvailabilityChecked ? false : now(date).isBefore(now(), "day");
    };

    const upcomingDatesList = upcomingDates
        ? upcomingDates.filter((date) => now(date).isSameOrAfter(next30Days, "days")).map((date) => now(date).toDate())
        : [];

    const hasUpcomingSlots = upcomingDatesList.length > 0 || shouldFetchUpcoming;
    return (
        <>
            <ArrivalRestrictions
                item={item}
                product={product}
                errors={arrivalErrors}
                packageId={packageId ?? item?.parentProduct?.id}
            />

            <div className="ui-arrival-date-time-picker">
                <div className="space-y-1">
                    <span className="text-sm font-bold">Date</span>
                    <div className="flex w-full items-center space-x-6">
                        <ArrivalDatePicker
                            month={month}
                            placement={placement}
                            value={value.date}
                            bookableDates={bookableDates}
                            unavailabilityMessagesByDate={unavailabilityMessagesByDate}
                            disabledDays={handleDisabledDays}
                            loadingDays={handleLoadingDays}
                            waitlistDates={isWaitlistedAllowed ? waitlistDates : undefined}
                            components={{
                                Footer: () => (
                                    <div className="flex flex-row justify-between space-x-2">
                                        <PastAvailabilitySwitch
                                            isChecked={isPastAvailabilityChecked}
                                            onChange={handlePastAvailabilityChange}
                                        />
                                        <OverrideFutureCutoffSwitch
                                            productId={product}
                                            isChecked={isOverrideFutureCutoffChecked}
                                            onChange={handleOverrideFutureCutoffChange}
                                        />
                                    </div>
                                ),
                            }}
                            upcomingDates={hasUpcomingSlots ? upcomingDatesList : undefined}
                            datePickerPreference={datePickerPreference}
                            lastScheduledDate={lastScheduledDate}
                            onChange={handleDayChange}
                            onMonthChange={debouncedOnMonthChange}
                        />
                    </div>
                </div>
                {hasTimeslot && (
                    <ArrivalTimePicker
                        value={value.time}
                        timeslots={timeslotsOfDay()}
                        allowWaitlist={allowWaitlist}
                        initialArrival={initialArrival}
                        minQuantity={quantity}
                        allowOverlap={allowOverlap}
                        onChange={handleTimeslotChange}
                    />
                )}
                {timeslot && timeslot.scheduleType === ScheduleType.TIME && isTimeslotInProgress(timeslot) && (
                    <div className="mt-4">
                        <Alert color="primary">
                            <CircleInfoIcon size="medium" /> This event is currently in progress and ends at{" "}
                            <span>{timeslotEndFormatted}</span>
                        </Alert>
                    </div>
                )}
                <div className={clsx("mt-4", showAllTimeslotsSwitch && "flex justify-between")}>
                    {showAddNewTimeSlot && !isUserAffiliate && (
                        <ManageActionButton
                            text="Add a new time"
                            disabled={isPartnerPackageProduct(productData) || productData?.seller.id !== seller.id}
                            onClick={createTimeslotModal.show}
                        />
                    )}
                    {showAllTimeslotsSwitch && (
                        <Switch.Group>
                            <Switch size="medium" isChecked={showAllTimes} onChange={handleShowAllTimesChange} />
                            <Switch.Label direction="right">Show all times</Switch.Label>
                        </Switch.Group>
                    )}
                </div>

                <CreateExperienceTimeslotModal
                    timeslot={timeslot ?? timeslots?.[0]}
                    isOpen={createTimeslotModal.isVisible}
                    experienceId={product}
                    initialValue={value.date ? now(value.date) : null}
                    showCapacity={showCapacity}
                    showAllTimes={showAllTimes}
                    disabledDays={handleNewTimeslotDisabledDays}
                    onClose={(timeslot: Timeslot, date?: Dayjs, shouldRefetch?: boolean) => {
                        // We need to reset offset to fetch new slots for the current month,
                        // since after changing month current month can be skipped on refetch.
                        offsetDate.current = initialOffset;

                        if (date && isDayjs(date)) {
                            const newTime = Number(date.format("Hmm"));
                            const payload: Arrival = { date: formatDate(date), time: null };

                            // TODO: We need to check if the time is actually created since `0` also means midnight
                            if (newTime >= 0) {
                                payload.time = newTime;
                            }

                            handleValueChange(payload, timeslot, true);
                            refetch();
                        } else if (shouldRefetch) {
                            refetch(); // refetch timeslots again to add the newly added slots
                        }

                        createTimeslotModal.hide();
                    }}
                />
            </div>
        </>
    );
};
